import { type CSSProperties, type JSX, useEffect, useState } from 'react';

export interface HeroClockProps {
  /** Whether to show the date line under the time. Defaults true. */
  showDate?: boolean;
  /** Optional caption line above the time (e.g., "Welcome to ops"). */
  caption?: string;
  /** Optional hero background image (Pro). */
  imageSrc?: string;
}

/**
 * Large-clock hero — fills the hero slab with the current time, with
 * an optional date line + caption. Updates every 15s (~ minute
 * resolution; we deliberately don't tick to seconds — a kiosk doesn't
 * need that precision and it'd defeat browser power management).
 *
 * The launcher's header already renders a small clock for tenants who
 * want it always-visible; this hero is for the case where the clock IS
 * the primary content (lobby tablet, signage display).
 */
export function HeroClock({
  showDate = true,
  caption,
  imageSrc,
}: HeroClockProps): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());

  // 15-second tick is a compromise between "looks live" and "doesn't
  // wake the radio every second on a battery-constrained kiosk." Mode
  // picker doesn't fire often; on display-on devices this is plenty.
  useEffect(() => {
    const handle = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(handle);
  }, []);

  const style: CSSProperties | undefined =
    imageSrc !== undefined
      ? {
          backgroundImage:
            `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.45) 100%), url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : undefined;

  return (
    <section
      className={`launcher-hero launcher-hero--clock${
        imageSrc !== undefined ? ' launcher-hero--has-image' : ''
      }`}
      {...(style && { style })}
      data-testid="launcher-hero"
      data-hero-kind="clock"
    >
      <div className="launcher-hero__clock-stack">
        {caption !== undefined && caption.length > 0 && (
          <p className="launcher-hero__clock-caption" data-testid="launcher-hero-caption">
            {caption}
          </p>
        )}
        <p
          className="launcher-hero__clock-time"
          data-testid="launcher-hero-time"
          aria-live="polite"
        >
          {formatTime(now)}
        </p>
        {showDate && (
          <p className="launcher-hero__clock-date" data-testid="launcher-hero-date">
            {formatDate(now)}
          </p>
        )}
      </div>
    </section>
  );
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(d: Date): string {
  return d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}
